import { ExpressionModel } from '../../Models/ExpressionModel';
import { PLACEHOLDER_MATCH_REGEX, PLACEHOLDER_REPLACE_REGEX } from '../../Models/StringTemplateModel';
import { isSystemTemplatePlaceholder } from '../../Models/TemplatePlaceholderRules';

const VOID_ELEMENTS = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
const RAW_TEXT_ELEMENTS = new Set(['script', 'style', 'pre', 'textarea']);

export function shouldPreserveRawHtml(content: string): boolean {
	return /<!doctype\b|<html\b|<head\b|<body\b|<style\b|<meta\b|<title\b|<link\b|<script\b|<table\b|<tr\b|<td\b|\sstyle\s*=/i.test(content);
}

export function getHtmlDoctype(content: string): string {
	return /^\s*<!doctype\b/i.test(content) ? '<!DOCTYPE html>\n' : '';
}

function escapeHtmlText(value: string): string {
	return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeHtmlAttribute(value: string): string {
	return escapeHtmlText(value).replace(/"/g, '&quot;');
}

function formatHtmlAttributes(element: Element): string {
	return Array.from(element.attributes)
		.map((attribute) => (attribute.value === '' ? attribute.name : `${attribute.name}="${escapeHtmlAttribute(attribute.value)}"`))
		.join(' ');
}

function formatHtmlNode(node: ChildNode, depth: number): string {
	const indent = '  '.repeat(depth);

	if (node.nodeType === Node.TEXT_NODE) {
		const text = node.textContent?.trim();
		return text ? `${indent}${escapeHtmlText(text)}` : '';
	}

	if (node.nodeType === Node.COMMENT_NODE) {
		return `${indent}<!--${node.textContent ?? ''}-->`;
	}

	if (node.nodeType !== Node.ELEMENT_NODE) {
		return '';
	}

	const element = node as Element;
	const tagName = element.tagName.toLowerCase();
	const attributes = formatHtmlAttributes(element);
	const openingTag = attributes ? `<${tagName} ${attributes}>` : `<${tagName}>`;

	if (VOID_ELEMENTS.has(tagName)) {
		return `${indent}${openingTag}`;
	}

	if (RAW_TEXT_ELEMENTS.has(tagName)) {
		return `${indent}${openingTag}${element.textContent ?? ''}</${tagName}>`;
	}

	const children = Array.from(element.childNodes).map((child) => formatHtmlNode(child, depth + 1)).filter(Boolean);
	if (children.length === 0) {
		return `${indent}${openingTag}</${tagName}>`;
	}

	return `${indent}${openingTag}\n${children.join('\n')}\n${indent}</${tagName}>`;
}

export function formatHtmlSource(content: string): string {
	const trimmed = content.trim();
	if (!trimmed) {
		return content;
	}

	const doc = new DOMParser().parseFromString(trimmed, 'text/html');
	const hasFullDocument = /<!doctype\b|<html\b|<head\b|<body\b/i.test(trimmed);

	if (hasFullDocument) {
		return `${getHtmlDoctype(trimmed)}${formatHtmlNode(doc.documentElement, 0)}`;
	}

	const formattedFragment = Array.from(doc.body.childNodes).map((node) => formatHtmlNode(node, 0)).filter(Boolean).join('\n');
	return formattedFragment || content;
}

export function getRawHtmlEditorContent(content: string): string {
	const doc = new DOMParser().parseFromString(content, 'text/html');
	return doc.body.innerHTML || content;
}

export function updateRawHtmlBodyContent(content: string, bodyContent: string): string {
	const doc = new DOMParser().parseFromString(content, 'text/html');
	doc.body.innerHTML = bodyContent;
	return `${getHtmlDoctype(content)}${doc.documentElement.outerHTML}`;
}

export function serializeHtmlDocument(content: string, doc: Document): string {
	return `${getHtmlDoctype(content)}${doc.documentElement.outerHTML}`;
}

export function htmlToContent(html: string, isTextMode: boolean): string {
	const parser = new DOMParser();
	const doc = parser.parseFromString(html, 'text/html');
	doc.querySelectorAll('span[data-expression-id]').forEach((el) => {
		const id = el.getAttribute('data-expression-id') ?? '';
		el.replaceWith(doc.createTextNode(`{{${id}}}`));
	});
	if (isTextMode) {
		doc.querySelectorAll('p').forEach((p) => {
			const frag = doc.createDocumentFragment();
			frag.append(...Array.from(p.childNodes));
			frag.appendChild(doc.createTextNode('\n'));
			p.replaceWith(frag);
		});
		doc.querySelectorAll('br').forEach((br) => br.replaceWith(doc.createTextNode('\n')));
		return (doc.body.textContent ?? '').replace(/\n$/, '');
	}
	return doc.body.innerHTML;
}

export function contentToHtml(content: string, placeholders: Record<string, ExpressionModel>, isTextMode: boolean): string {
	const replaceTokens = (text: string) =>
		text.replace(PLACEHOLDER_REPLACE_REGEX, (_, id) => {
			const configured = id in placeholders;
			return `<span data-expression-id="${id}" data-configured="${configured}">${id}</span>`;
		});

	if (isTextMode) {
		const lines = content === '' ? [''] : content.split('\n');
		return lines.map((line) => `<p>${replaceTokens(line)}</p>`).join('');
	}

	const doc = new DOMParser().parseFromString(content, 'text/html');
	const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, {
		acceptNode: (node) => (PLACEHOLDER_MATCH_REGEX.test(node.textContent ?? '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
	});
	const nodes: Text[] = [];
	while (walker.nextNode()) {
		nodes.push(walker.currentNode as Text);
	}

	nodes.forEach((node) => {
		const fragment = doc.createDocumentFragment();
		let lastIndex = 0;
		const text = node.textContent ?? '';
		text.replace(PLACEHOLDER_REPLACE_REGEX, (match, id, offset) => {
			fragment.appendChild(doc.createTextNode(text.slice(lastIndex, offset)));
			const span = doc.createElement('span');
			span.setAttribute('data-expression-id', id);
			span.setAttribute('data-configured', String(id in placeholders));
			span.textContent = id;
			fragment.appendChild(span);
			lastIndex = offset + match.length;
			return match;
		});
		fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
		node.replaceWith(fragment);
	});

	return doc.body.innerHTML || content;
}

export function contentToRawHtmlPreview(content: string, placeholders: Record<string, ExpressionModel>): string {
	const doc = new DOMParser().parseFromString(content, 'text/html');
	const target = doc.body;
	const walker = doc.createTreeWalker(target, NodeFilter.SHOW_TEXT, {
		acceptNode: (node) => {
			const parent = node.parentElement;
			if (!parent || ['SCRIPT', 'STYLE', 'TITLE'].includes(parent.tagName)) {
				return NodeFilter.FILTER_REJECT;
			}

			return PLACEHOLDER_MATCH_REGEX.test(node.textContent ?? '') ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
		},
	});

	const nodes: Text[] = [];
	while (walker.nextNode()) {
		nodes.push(walker.currentNode as Text);
	}

	nodes.forEach((node) => {
		const fragment = doc.createDocumentFragment();
		let lastIndex = 0;
		const text = node.textContent ?? '';
		text.replace(PLACEHOLDER_REPLACE_REGEX, (match, id, offset) => {
			fragment.appendChild(doc.createTextNode(text.slice(lastIndex, offset)));
			const chip = doc.createElement('span');
			chip.className = `expression-placeholder-preview ${id in placeholders ? 'configured' : 'unconfigured'}`;
			if (isSystemTemplatePlaceholder(id)) {
				chip.className = 'expression-placeholder-preview configured';
			}
			chip.dataset.expressionId = id;
			chip.textContent = isSystemTemplatePlaceholder(id) ? 'Child content' : id;
			fragment.appendChild(chip);
			lastIndex = offset + match.length;
			return match;
		});
		fragment.appendChild(doc.createTextNode(text.slice(lastIndex)));
		node.replaceWith(fragment);
	});

	const style = doc.createElement('style');
	style.textContent = `
		.expression-placeholder-preview {
			display: inline-block;
			box-sizing: border-box;
			max-width: 100%;
			margin: 0 2px;
			padding: 1px 7px;
			border: 1px solid #ff9800;
			border-radius: 999px;
			background: #ff9800;
			color: #111;
			font-family: Arial, Helvetica, sans-serif;
			font-size: 12px;
			font-weight: 600;
			line-height: 18px;
			vertical-align: baseline;
			white-space: nowrap;
		}
		.expression-placeholder-preview.configured {
			border-color: #1976d2;
			background: #1976d2;
			color: #fff;
		}
	`;
	doc.head.appendChild(style);

	return `${getHtmlDoctype(content)}${doc.documentElement.outerHTML}`;
}
